import React, { FC, useState } from 'react';
import styles from './Toast.module.scss';

import * as RadixToast from '@radix-ui/react-toast';
import { IconName } from '@/components/atoms/Icon/iconMap';
import clsx from 'clsx';
import { Button, ButtonVariants } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { IconButton, IconButtonVariants } from '@/components/atoms/IconButton';
import { Typographies, Typography } from '@/components/atoms/Typography';

type ToastType = 'info' | 'success' | 'error';
type ToastDismissType = 'auto' | 'manual' | 'action';

const iconNames: Record<ToastType, IconName> = {
  info: 'general/information-circle/24',
  success: 'general/checkmark-circle/24',
  error: 'general/alert-circle/24',
};

const dismissTime: Record<ToastDismissType, number> = {
  auto: 4000,
  manual: 9999999, // just to not close the toast
  action: 8000,
};

const HIDE_ANIMATION_MS = 200; // must match CSS hide animation duration

type ToastBaseProps = {
  id?: string;
  type?: ToastType;
  title: string;
  onClose?: () => void;
};

interface ToastAutoProps extends ToastBaseProps {
  dismissType?: 'auto';
  action?: never;
}

interface ToastManualProps extends ToastBaseProps {
  dismissType: 'manual';
  action?: never;
}

interface ToastActionProps extends ToastBaseProps {
  dismissType: 'action';
  action: { label: string; onClick: () => void };
}

type ToastProps = ToastAutoProps | ToastManualProps | ToastActionProps;

const Toast: FC<ToastProps> = ({ type = 'info', dismissType = 'auto', title, action, onClose }) => {
  const [open, setOpen] = useState(true);

  return (
    <RadixToast.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setOpen(false);
          window.setTimeout(() => {
            if (onClose) {
              onClose();
            }
          }, HIDE_ANIMATION_MS);
        } else {
          setOpen(true);
        }
      }}
      duration={dismissTime[dismissType]}
      className={clsx(styles.toast, styles[type], styles[dismissType])}
    >
      <Icon name={iconNames[type]} size={24} className={styles.icon} />
      <Typography variant={Typographies.LABEL_2_SEMIBOLD}>{title}</Typography>

      {action && (
        <RadixToast.Action asChild altText={action.label}>
          <Button
            variant={ButtonVariants.TEXT}
            size="big"
            className={styles.action_button}
            onClick={() => {
              action.onClick();
              setOpen(false);
            }}
          >
            {action.label}
          </Button>
        </RadixToast.Action>
      )}

      {dismissType === 'manual' && (
        <RadixToast.Close asChild>
          <IconButton
            variant={IconButtonVariants.SIMPLE}
            size="small"
            className={styles.close_button}
            Icon={() => <Icon name="navigation/close/16" size={16} color="var(--icon-secondary)" />}
            onClick={() => {
              setOpen(false);
            }}
          />
        </RadixToast.Close>
      )}
    </RadixToast.Root>
  );
};

export { Toast, type ToastProps };
